import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  SvgIcon,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import HomeIcon from '../../../../assets/Home.png';
import GroupIcon from '../../../../assets/Group.svg';
import TicketIcon from '../../../../assets/Ticket.svg';
import MessagesIcon from '../../../../assets/message.svg';
import BetaLogo from '../../../../assets/BetaLogo.png';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const HomeLink = ({ currentPage, animateOnHome }) => {
  const isActive = currentPage === 'LandingPage';
  return (
    <NamedLink
      name="LandingPage"
      className={classNames(css.navLink, { [css.homeLoadAnimation]: animateOnHome })}
      style={{ '--navAnimationDelay': '0ms' }}
    >
      <div className={css.navLinkContent}>
        <div className={css.navIcon}>
          <img src={HomeIcon} alt="Home" style={{ width: 24, height: 24, objectFit: 'contain' }} />
        </div>
        <span className={css.navLabel}>
          <FormattedMessage id="TopbarDesktop.home" />
        </span>
        <div className={classNames(css.navUnderline, { [css.navUnderlineActive]: isActive })} />
      </div>
    </NamedLink>
  );
};

const CrewsLink = ({ currentPage, isAuthenticated, history, currentUser, animateOnHome }) => {
  const isActive =
    currentPage === 'MyCrewsPage' ||
    currentPage === 'CrewsPage' ||
    currentPage === 'FindCrewsPage' ||
    currentPage === 'CrewPage' ||
    currentPage === 'CrewsSelectionPage' ||
    currentPage === 'CreateCrewPage' ||
    currentPage === 'CrewPostPage' ||
    currentPage === 'TicketBidsPage' ||
    currentPage === 'BidsPage' ||
    currentPage === 'FindLinkUpsPage' ||
    currentPage === 'LinkUpChatPage' ||
    currentPage === 'CreateLinkUpPage' ||
    currentPage === 'CreatePostPage';

  const handleCrewsClick = e => {
    e.preventDefault();
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    // Check email verification for authenticated users
    const isEmailVerified = currentUser?.attributes?.emailVerified;
    // If email is not verified (false, null, undefined), redirect to verification
    if (isEmailVerified !== true) {
      history.push('/verify-email');
      return;
    }

    // If authenticated and email verified, proceed to crews page
    history.push('/crews');
  };

  return (
    <button
      className={classNames(css.navLink, { [css.homeLoadAnimation]: animateOnHome })}
      onClick={handleCrewsClick}
      style={{
        '--navAnimationDelay': '120ms',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div className={css.navLinkContent}>
        <div className={css.navIcon}>
          <SvgIcon
            src={GroupIcon}
            alt="Crews"
            width={24}
            height={24}
            style={{ display: 'block' }}
          />
        </div>
        <span className={css.navLabel}>
          <FormattedMessage id="TopbarDesktop.crews" />
        </span>
        <div className={classNames(css.navUnderline, { [css.navUnderlineActive]: isActive })} />
      </div>
    </button>
  );
};

const MyTicketsLink = ({
  currentPage,
  isAuthenticated,
  history,
  currentUser,
  location,
  animateOnHome,
}) => {
  const isActive = currentPage === 'ManageListingsPage';

  const handleMyTicketsClick = e => {
    e.preventDefault();
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    // Check email verification for authenticated users
    const isEmailVerified = currentUser?.attributes?.emailVerified;
    // If email is not verified (false, null, undefined), redirect to verification
    if (isEmailVerified !== true) {
      history.push('/verify-email');
      return;
    }
    history.push('/user-settings?section=my-listings');
  };

  return (
    <button
      className={classNames(css.navLink, { [css.homeLoadAnimation]: animateOnHome })}
      onClick={handleMyTicketsClick}
      style={{
        '--navAnimationDelay': '240ms',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div className={css.navLinkContent}>
        <div className={css.navIcon}>
          <SvgIcon
            src={TicketIcon}
            alt="My Tickets"
            width={24}
            height={24}
            style={{ display: 'block' }}
          />
        </div>
        <span className={css.navLabel}>
          <FormattedMessage id="TopbarDesktop.myTickets" />
        </span>
        <div className={classNames(css.navUnderline, { [css.navUnderlineActive]: isActive })} />
      </div>
    </button>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings, currentUser, history }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const handleInboxClick = e => {
    e.preventDefault();
    // Check email verification for authenticated users
    const isEmailVerified = currentUser?.attributes?.emailVerified;
    if (!isEmailVerified) {
      history.push('/verify-email');
      return;
    }

    // If email verified, proceed to inbox
    const tab = currentUserHasListings ? 'sales' : 'orders';
    history.push(`/inbox/${tab}`);
  };

  return (
    <button
      className={css.topbarLink}
      onClick={handleInboxClick}
      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
    >
      <span className={css.topbarLinkLabel}>
        <SvgIcon
          src={MessagesIcon}
          alt="Messages"
          width={24}
          height={24}
          style={{ display: 'block' }}
        />
        {notificationDot}
      </span>
    </button>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, history, notificationCount }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const handleProfileClick = () => {
    // Check if user is authenticated
    if (!currentUser) {
      history.push('/login');
      return;
    }

    // Allow access to settings regardless of email verification status
    // Email verification will be handled within the settings page for gated content
    history.push('/user-settings');
  };

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div onClick={handleProfileClick} style={{ position: 'relative', cursor: 'pointer' }}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          {notificationDot}
        </div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    rootClassName,
    config,
    customLinks,
    currentUserHasListings,
    notificationCount = 0,
    streamUnseenCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    history,
    currentPage,
    currentUser,
    location,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  // Check if we're on authentication pages (login/signup/confirm)
  const isAuthenticationPage = [
    'AuthenticationPage',
    'LoginPage',
    'SignupPage',
    'SignupForUserTypePage',
    'ConfirmPage',
    'ProfileSetupPage',
    'VibesPage',
  ].includes(currentPage);
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className, {
    [css.authDesktopRoot]: isAuthenticationPage,
  });
  const animateHomeNavigation = currentPage === 'LandingPage';

  const navigationLinksMaybe = !isAuthenticationPage ? (
    <div className={css.navigationContainer}>
      <HomeLink currentPage={currentPage} animateOnHome={animateHomeNavigation} />
      <CrewsLink
        currentPage={currentPage}
        isAuthenticated={isAuthenticated}
        history={history}
        currentUser={currentUser}
        animateOnHome={animateHomeNavigation}
      />
      <MyTicketsLink
        currentPage={currentPage}
        isAuthenticated={isAuthenticated}
        history={history}
        currentUser={currentUser}
        location={location}
        animateOnHome={animateHomeNavigation}
      />
    </div>
  ) : null;

  const inboxLinkMaybe =
    authenticatedOnClientSide && !isAuthenticationPage ? (
      <InboxLink
        notificationCount={notificationCount}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        history={history}
      />
    ) : null;

  const profileMenuMaybe =
    authenticatedOnClientSide && !isAuthenticationPage ? (
      <ProfileMenu
        currentPage={currentPage}
        currentUser={currentUser}
        onLogout={onLogout}
        history={history}
        notificationCount={0}
      />
    ) : null;

  const signupLinkMaybe =
    isAuthenticatedOrJustHydrated || isAuthenticationPage ? null : (
      <NamedLink className={css.topbarLink} name="SignupPage">
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.signup" />
        </span>
      </NamedLink>
    );

  const loginLinkMaybe =
    isAuthenticatedOrJustHydrated || isAuthenticationPage ? null : (
      <NamedLink className={css.topbarLink} name="LoginPage">
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.login" />
        </span>
      </NamedLink>
    );

  const searchFormMaybe =
    showSearchForm && !isAuthenticationPage ? (
      <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        desktopInputRoot={css.topbarSearchWithLeftPadding}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      />
    ) : (
      <div
        className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
          [css.takeAvailableSpace]: giveSpaceForSearch,
        })}
      />
    );

  return (
    <nav className={classes}>
      <div className={css.logoWithBeta}>
        <LinkedLogo
          className={css.logoLink}
          logoImageDesktop={BetaLogo}
          logoSettings={{ format: 'image', height: 36 }}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
      </div>

      {navigationLinksMaybe}

      {searchFormMaybe}

      {!isAuthenticationPage && (
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          isAuthenticated={authenticatedOnClientSide}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          history={history}
          currentUser={currentUser}
        />
      )}

      {inboxLinkMaybe}
      {profileMenuMaybe}
      {signupLinkMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
